'use client';

import React from 'react';
import Markdown, { MarkdownToJSX } from 'markdown-to-jsx';
import { cn } from '@/lib/utils';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({ content, className }) => {
  if (!content) return null;

  const markdownOverrides: MarkdownToJSX.Options = {
    overrides: {
      table: {
        component: ({ children, ...props }: any) => (
          <div className="w-full overflow-x-auto my-2">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-[#222222] shadow-xs">
                <table
                  style={{
                    width: '100%',
                    minWidth: '100%',
                    borderCollapse: 'collapse',
                  }}
                  {...props}
                >
                  {children}
                </table>
              </div>
            </div>
          </div>
        ),
      },
      code: {
        component: ({ children, ...props }: any) => {
          return (
            <div className="w-full overflow-x-auto my-2">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-[#222222] shadow-xs">
                  <code {...props}>{children}</code>
                </div>
              </div>
            </div>
          );
        },
      },
    },
  };

  return (
    <div
      className={cn(
        'markdown-content',
        // 表头样式
        '[&_thead]:bg-gray-50 dark:[&_thead]:bg-[#1a1a1a]',
        '[&_th]:px-4 [&_th]:py-3 [&_th]:text-center [&_th]:text-xs [&_th]:font-medium',
        '[&_th]:tracking-wider [&_th]:text-text-primary dark:[&_th]:text-text-tertiary',
        '[&_th]:border-b [&_th]:border-gray-200 dark:[&_th]:border-[#222222]',
        '[&_th]:whitespace-nowrap',
        // 表体样式
        '[&_tbody_tr]:border-b [&_tbody_tr]:border-gray-200 dark:[&_tbody_tr]:border-[#171717]',
        '[&_tbody_tr:hover]:bg-gray-50 dark:[&_tbody_tr:hover]:bg-gray-800',
        '[&_tbody_tr:last-child]:border-b-0',
        // 单元格样式
        '[&_td]:px-4 [&_td]:py-3 [&_td]:text-[12px] [&_td]:text-center',
        '[&_td]:text-text-primary dark:[&_td]:text-text-tertiary [&_td]:whitespace-nowrap',
      )}
    >
      <Markdown
        className={cn(
          'max-w-none break-words text-[12px] font-normal text-[#646464] dark:text-[#757575]',
          '[&_h1]:mb-2 [&_h1]:text-[20px] [&_h1]:font-semibold [&_h1]:leading-tight [&_h1]:text-text-primary',
          '[&_h2]:mb-[12px] [&_h2]:mt-4 [&_h2]:text-[16px] [&_h2]:font-semibold [&_h2]:leading-snug [&_h2]:text-text-primary',
          '[&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-[14px] [&_h3]:font-[600] [&_h3]:leading-snug [&_h3]:text-text-primary',
          '[&_h4]:mb-1 [&_h4]:mt-2 [&_h4]:text-[13px] [&_h4]:font-semibold [&_h4]:text-text-primary',
          '[&_p]:mb-2 [&_p]:leading-5',
          '[&_strong]:font-semibold [&_strong]:text-text-primary',
          '[&_a]:text-brand [&_a]:underline [&_a]:underline-offset-3 hover:[&_a]:text-brand-hover',
          '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
          '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
          '[&_li]:my-1 [&_li]:leading-5',
          '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-border-card [&_blockquote]:pl-3',
          '[&_code]:rounded [&_code]:bg-bg-card [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[11px] [&_code]:text-text-primary',
          '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-bg-card [&_pre]:p-0',
          '[&_pre_code]:block [&_pre_code]:bg-transparent [&_pre_code]:p-3',
          '[&_table]:my-0 [&_table]:w-full',
          className,
        )}
        options={markdownOverrides}
      >
        {content}
      </Markdown>
    </div>
  );
};

export default MarkdownContent;
